import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { MouseEventHandler, forwardRef } from 'react';
import styled, { CSSObject, css, useTheme } from 'styled-components';
import { UrlObject } from 'url';

import {
  EActionAttribute,
  EColor,
  ELink,
  ELinkAnimation,
  EPosition,
  ThemeProps,
  WithThemeProps,
} from '@core/type';
import { getLinkColorProps, getPaletteHandlers } from '@core/util';

import { iconSize } from '../constants';
import { Icon, IconWrapper } from '../icon/Icon';
import { IconContainerProps, IconElement, LinkProps } from '../link/interface-link';
import { Typography } from '../typography';
import { StyledLinkProps } from './interface-link-translated';

const FILTER_PROPS = [
  'isUnderlined',
  'isUnderlinedHover',
  'isChangingColorHover',
  'hiddenTextOnMobile',
  'isIconFill',
  'isIconStroke',
  'color',
  'animation',
  'hasIcon',
  'textTransform',
  'top',
  'hoverColor',
  'bottom',
  'left',
  'right',
  'letterSpacing',
  'variant',
  'fontSize',
  'fontWeight',
  'lineHeight',
  'enableAnimation',
  'alignItems',
  'textAlign',
  'wordBreak',
  'shallow',
];

const StyledNextLink = styled(NextLink).withConfig({
  shouldForwardProp: (prop) => !FILTER_PROPS.includes(prop),
})<StyledLinkProps & NextLinkProps>(
  ({
    theme: {
      palette,
      typography: { fontWeight: fontWeightVariant, defaultVariant, ...restTypography },
      structure: {
        link: {
          base,
          transition,
          svg: svgBase,
          icon: {
            [ELinkAnimation.ARROW_RIGHT]: arrowRightStyles,
            [ELinkAnimation.ARROW_DOWN]: arrowDownStyles,
          },
          underlineFirstPath,
          underlineSecondPath,
          text: textStyles,
          variant,
        },
      },
    },
    ...restProps
  }: StyledLinkProps & WithThemeProps) => {
    const {
      isUnderlined,
      isUnderlinedHover = true,
      isChangingColorHover = true,
      hiddenTextOnMobile,
      isIconFill = true,
      isIconStroke,
      color,
      animation,
      hasIcon,
      textTransform,
      top,
      hoverColor: hoverColorProps,
      bottom,
      left,
      right,
      letterSpacing,
      variant: typographyVariant = defaultVariant,
      fontSize = restTypography[typographyVariant].fontSize,
      fontWeight = restTypography[typographyVariant].fontWeight,
      lineHeight = restTypography[typographyVariant].lineHeight,
      display,
      enableAnimation,
      alignItems,
      textAlign,
      wordBreak,
      pointerEvents,
      verticalAlign,
    } = restProps;

    const { getColor } = getPaletteHandlers(palette);
    const {
      color: textColor,
      hover: { color: hoverTextColor },
    } = getLinkColorProps({ color, palette });
    const hoverColor = getColor(hoverColorProps);

    const variantTextRules = hiddenTextOnMobile && variant[ELink.WITHOUT_TEXT_ON_MOBILE];

    const iconPresentStyles = css`
      ${Typography.toString()} {
        ${{ transition } as CSSObject};
        ${textStyles as CSSObject};
        ${underlineFirstPath as CSSObject};
        ${isUnderlined && (underlineSecondPath as CSSObject)};
        ${variantTextRules as CSSObject};
      }

      svg,
      svg * {
        ${{ transition } as CSSObject};
      }

      svg * {
        ${isIconFill && `fill: ${textColor}`};
        ${isIconStroke && `stroke: ${textColor}`};
      }

      ${IconWrapper} {
        ${enableAnimation
          ? animation === ELinkAnimation.ARROW_RIGHT && arrowRightStyles
          : animation === ELinkAnimation.ARROW_DOWN && arrowDownStyles};
      }

      &:hover {
        ${Typography.toString()} {
          ${underlineSecondPath as CSSObject};
        }

        ${IconWrapper} {
          ${animation === ELinkAnimation.ARROW_RIGHT && arrowRightStyles};
          ${animation === ELinkAnimation.ARROW_DOWN && arrowDownStyles};
        }

        svg,
        svg * {
          ${svgBase as CSSObject};
        }

        ${isChangingColorHover &&
        `svg * {
          ${isIconFill && `fill: ${hoverColor ?? hoverTextColor}`};
          ${isIconStroke && `stroke: ${hoverColor ?? hoverTextColor}`};
        }`}
      }
    `;

    return css`
      ${base as CSSObject};
      font-weight: ${fontWeightVariant[fontWeight]};
      font-size: ${fontSize};
      line-height: ${lineHeight};
      ${textTransform && ({ textTransform } as CSSObject)};
      color: ${textColor};
      top: ${top};
      bottom: ${bottom};
      left: ${left};
      right: ${right};
      display: ${display};
      align-items: ${alignItems};
      letter-spacing: ${letterSpacing};
      text-align: ${textAlign};
      word-break: ${wordBreak};
      pointer-events: ${pointerEvents};
      vertical-align: ${verticalAlign};

      ${!hasIcon && (underlineFirstPath as CSSObject)};
      ${!hasIcon && ({ transition } as CSSObject)};
      ${isUnderlined && (underlineSecondPath as CSSObject)};
      ${hasIcon && iconPresentStyles};

      &:focus {
        color: ${textColor};
      }

      &:hover {
        ${isUnderlinedHover && (underlineSecondPath as CSSObject)};
        ${isChangingColorHover && `color: ${hoverColor ?? hoverTextColor}`};
      }
    `;
  },
);

export const StyledTranslatedLink = forwardRef<
  HTMLAnchorElement,
  NextLinkProps &
    LinkProps &
    IconElement & {
      onClick?: MouseEventHandler<HTMLAnchorElement>;
      href:
        | string
        | {
            pathname: string;
            query: NodeJS.Dict<string | string[]>;
          };
    }
>((props, ref) => {
  const {
    startIcon,
    endIcon,
    color = EColor.PRIMARY,
    children,
    fontSize,
    fontWeight,
    animation,
    hiddenTextOnMobile,
    isIconFill,
    isIconStroke,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    onClick,
    href,
    isUnderlinedHover = true,
    isChangingColorHover = true,
    textTransform,
    lineHeight,
    target,
    top,
    bottom,
    left,
    right,
    variant,
    alignItems,
    letterSpacing,
    display,
    isUnderlined,
    enableAnimation,
    hoverColor,
    buttonType,
    textAlign,
    wordBreak,
    isDraggable = true,
    forwardedAs,
    locale,
    'aria-label': ariaLabel,
    pointerEvents,
    rel,
  } = props;

  const restProps: Record<string, UrlObject | string> = {};
  if (forwardedAs && typeof forwardedAs === 'string') {
    restProps.forwardedAs = forwardedAs;
  }

  const hasIcon = !!startIcon || !!endIcon;

  return (
    <StyledNextLink
      color={color}
      hasIcon={hasIcon}
      type={buttonType}
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment */
      onClick={onClick}
      animation={animation}
      variant={variant}
      fontSize={fontSize}
      fontWeight={fontWeight}
      hiddenTextOnMobile={hiddenTextOnMobile}
      isIconFill={isIconFill}
      isIconStroke={isIconStroke}
      ref={ref}
      href={href}
      textTransform={textTransform}
      lineHeight={lineHeight}
      target={target}
      top={top}
      left={left}
      right={right}
      bottom={bottom}
      isUnderlinedHover={isUnderlinedHover}
      isUnderlined={isUnderlined}
      display={display}
      isChangingColorHover={isChangingColorHover}
      enableAnimation={enableAnimation}
      alignItems={alignItems}
      hoverColor={hoverColor}
      letterSpacing={letterSpacing}
      textAlign={textAlign}
      wordBreak={wordBreak}
      draggable={isDraggable ? undefined : false}
      locale={locale}
      aria-label={ariaLabel}
      pointerEvents={pointerEvents}
      rel={rel}
      {...restProps}
    >
      {children}
    </StyledNextLink>
  );
});

StyledTranslatedLink.displayName = 'StyledTranslatedLink';

export const NextLinkStyledAsLink = forwardRef<
  HTMLAnchorElement,
  NextLinkProps &
    LinkProps &
    IconElement & {
      onClick?: MouseEventHandler<HTMLAnchorElement>;
      href:
        | string
        | {
            pathname: string;
            query: NodeJS.Dict<string | string[]>;
          };
    }
>((props, ref) => {
  const {
    structure: {
      link: {
        icon: { base: iconStyles },
        transition,
        indent,
      },
    },
  } = useTheme() as ThemeProps;

  const {
    startIcon,
    endIcon,
    iconSize,
    iconColor,
    children,
    fontSize,
    fontWeight,
    lineHeight,
    variant,
    iconStrokeWidth,
    iconPosition,
    iconOrientation,
    iconMargin = indent,
    iconTop = iconStyles.top,
    forwardedAs,
    rel,
  } = props;

  const restProps: Record<string, UrlObject | string> = {};
  if (forwardedAs && typeof forwardedAs === 'string') {
    restProps.forwardedAs = forwardedAs;
  }

  const hasIcon = !!startIcon || !!endIcon;

  return (
    <StyledTranslatedLink {...props} rel={rel} {...restProps} ref={ref}>
      {startIcon && (
        <Icon
          {...iconStyles}
          strokeWidth={iconStrokeWidth}
          transition={transition}
          type={startIcon}
          size={iconSize}
          marginRight={iconMargin}
          color={iconColor}
          orientation={iconOrientation}
          top={iconTop}
        />
      )}
      {hasIcon ? (
        <Typography
          as={EActionAttribute.SPAN}
          variant={variant}
          fontSize={fontSize}
          fontWeight={fontWeight}
        >
          {children}
        </Typography>
      ) : (
        children
      )}
      {endIcon && (
        <IconContainer
          variant={variant}
          lineHeight={lineHeight}
          position={iconPosition}
          size={iconSize}
        >
          <Icon
            {...iconStyles}
            transition={transition}
            type={endIcon}
            size={iconSize}
            marginLeft={iconMargin}
            color={iconColor}
            orientation={iconOrientation}
            top={iconTop}
          />
        </IconContainer>
      )}
    </StyledTranslatedLink>
  );
});

NextLinkStyledAsLink.displayName = 'NextLinkStyledAsLink';

const IconContainer = styled.div<IconContainerProps>(
  ({
    theme: {
      typography: { defaultVariant, ...restTypography },
    },
    variant,
    position,
    lineHeight: lineHeightProps,
    size: sizeProps,
  }: IconContainerProps & WithThemeProps) => {
    const size = iconSize[sizeProps];
    const lineHeight = lineHeightProps
      ? lineHeightProps
      : restTypography[variant ? variant : defaultVariant].lineHeight;

    if (position === EPosition.TOP_RIGHT) {
      return css`
        margin-left: 0;
        position: absolute;
        top: calc(-${lineHeight} / 2 + 4px);
        right: calc(-${size} - 6px);
      `;
    }
    return css`
      position: relative;
      display: inline;
    `;
  },
);
